import { getFindingInvestigation } from "./alertTriageClient.js";
import { runQuery, rowsToObjects } from "./analyticsClient.js";
import { discoveryPreviewsSpec, discoveryDetailSpec, discoveryReviewAttributionSpec, validateDiscoveryDetail } from "./discoveryQueries.js";
import { normalizeFindingRow, mergeFindingDetail } from "./alertsQueue.js";
import { MAX_FINDINGS } from "./findingsReviewPacket.js";

const changed = () => new Error("The investigation changed. Reload it and choose findings again.");
const tooMany = () => new Error(`Choose up to ${MAX_FINDINGS} findings for one review. Nothing was sent to the model.`);

// Read saved membership before analytics details so session grouping can never
// pull uncollected findings into a review. Every page belongs to one revision.
export async function loadInvestigationReviewInput({
    incidentId, version, findingIds = null, contextKey, signal, onProgress,
    getInvestigation = getFindingInvestigation, query = runQuery, decode = rowsToObjects,
}) {
    const startedAt = new Date().toISOString();
    const wanted = findingIds === null ? null : new Set(findingIds);
    if (wanted?.size === 0) throw new Error("Choose at least one finding to review.");
    if (wanted?.size > MAX_FINDINGS) throw tooMany();
    const members = [], seen = new Set(), cursors = new Set();
    let token = "", record;
    for (let page = 0; page < 100; page++) {
        signal?.throwIfAborted();
        record = await getInvestigation(incidentId, { memberPageToken: token, signal });
        signal?.throwIfAborted();
        if (record.incidentId !== incidentId || record.version !== version) throw changed();
        // Findings set aside were examined and judged to need nothing further, so
        // they leave the review scope with the working list.
        const active = record.findingCount - (record.setAsideCount ?? 0);
        if (!wanted && active > MAX_FINDINGS) throw tooMany();
        for (const member of record.findings) {
            if (seen.has(member.finding_id)) throw changed();
            seen.add(member.finding_id);
            if (member.setAsideAtMs) continue;
            if (!wanted || wanted.has(member.finding_id)) members.push(member);
        }
        if (members.length > MAX_FINDINGS) throw tooMany();
        onProgress?.({ phase: "members", count: members.length });
        if (wanted && members.length === wanted.size) break;
        if (!record.nextMemberPageToken) break;
        token = record.nextMemberPageToken;
        if (cursors.has(token) || page === 99) throw changed();
        cursors.add(token);
    }
    if (members.length !== (wanted?.size ?? record.findingCount - (record.setAsideCount ?? 0))) throw changed();
    if (!members.length) throw new Error(record.setAsideCount ? "Every finding in this investigation is set aside. Restore one to review it." : "This investigation has no findings to review.");
    const windows = new Map(), width = 89 * 86400000;
    for (const member of members) {
        const at = Date.parse(member.completed_at);
        if (!Number.isFinite(at)) throw new Error("A saved finding has no valid review time. Reload the investigation.");
        const bucket = Math.floor(at / width);
        if (!windows.has(bucket)) windows.set(bucket, []);
        windows.get(bucket).push(member);
    }
    const batches = [...windows].flatMap(([bucket, refs]) => Array.from({ length: Math.ceil(refs.length / 50) }, (_, i) => ({ bucket, refs: refs.slice(i * 50, i * 50 + 50) })));
    const findings = [], attribution = {};
    let attributionUnavailable = false;
    let detailUnavailable = 0;
    for (let i = 0; i < batches.length; i += 2) {
        signal?.throwIfAborted();
        const results = await Promise.all(batches.slice(i, i + 2).map(async ({ bucket, refs }) => {
            const range = { absolute: { start: new Date(bucket * width).toISOString(), end: new Date((bucket + 1) * width).toISOString() } };
            const spec = discoveryPreviewsSpec(range, refs);
            // Reuse the detail projection with the exact-reference batch filter.
            spec.output = discoveryDetailSpec(range, refs[0]).output;
            const rows = decode(await query(spec, { signal, requestContext: contextKey }));
            if (rows.some((r) => !refs.some((m) => m.finding_id === r.finding_id))) throw new Error("Finding details did not match the saved selection.");
            try {
                const agents = decode(await query(discoveryReviewAttributionSpec(range, refs), { signal, requestContext: contextKey }));
                signal?.throwIfAborted();
                if (agents.length > 500 || agents.some((r) => !refs.some((m) => m.finding_id === r.finding_id && m.trace_id === r.trace_id && m.report_id === r.report_id))) throw new Error("Agent attribution is incomplete.");
                for (const row of agents) {
                    const type = String(row.agent_type || "").trim();
                    if (!type || type.toLowerCase() === "unspecified") continue;
                    attribution[row.trace_id] ??= { types: new Set() };
                    attribution[row.trace_id].types.add(type);
                }
            } catch (error) {
                if (signal?.aborted) throw error;
                attributionUnavailable = true;
            }
            return refs.map((ref) => {
                const row = validateDiscoveryDetail(rows.filter((r) => r.finding_id === ref.finding_id), ref);
                if (!row) detailUnavailable++;
                return mergeFindingDetail(normalizeFindingRow({ ...ref, ...row }), row || {});
            });
        }));
        signal?.throwIfAborted();
        findings.push(...results.flat());
        onProgress?.({ phase: "details", count: findings.length, total: members.length });
    }
    const latest = await getInvestigation(incidentId, { signal });
    signal?.throwIfAborted();
    if (latest.incidentId !== incidentId || latest.version !== version) throw changed();
    return {
        selection: { type: "collection", label: record.title, findingRefs: members.map((m) => ({ findingId: m.finding_id, traceId: m.trace_id, reportId: m.report_id })) },
        findings, members, attribution: Object.fromEntries(Object.entries(attribution).map(([id, { types }]) => [id, { agentType: [...types].sort().join(" | ") } ])),
        collectedAt: { start: startedAt, end: new Date().toISOString() },
        notes: { members: members.length, membersTruncated: false, detailUnavailable, attributionUnavailable },
    };
}
